import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, IconButton, Button, Chip, CircularProgress,
    Divider, InputAdornment, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, List, ListItemButton, ListItemText, ListItemIcon, Badge, LinearProgress,
    Checkbox, Select, MenuItem, FormControl
} from '@mui/material';
import {
    Send as SendIcon, SimCard as SimCardIcon, Sms as SmsIcon, Delete as DeleteIcon,
    Add as AddIcon, Refresh as RefreshIcon, ContentCopy as CopyIcon,
    CheckCircle as CheckIcon, Error as ErrorIcon, Schedule as PendingIcon,
    History as HistoryIcon, SelectAll as SelectAllIcon, SendAndArchive as BulkSendIcon,
    LocationOn as SiteIcon, DoneAll as SelectModeIcon, Close as CloseIcon,
    Groups as GroupIcon, PrecisionManufacturing as MachineIcon
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import client from '../../api/client';

const pulse = keyframes`0%{box-shadow:0 0 0 0 rgba(37,99,235,0.4)}70%{box-shadow:0 0 0 10px rgba(37,99,235,0)}100%{box-shadow:0 0 0 0 rgba(37,99,235,0)}`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const MessageBubble = styled(Box)(({ direction }) => ({
    maxWidth: '75%', padding: '12px 16px', borderRadius: direction === 'SENT' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    background: direction === 'SENT' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#f1f5f9',
    color: direction === 'SENT' ? '#fff' : '#1e293b', animation: `${fadeIn} 0.3s ease`,
    boxShadow: direction === 'SENT' ? '0 2px 8px rgba(37,99,235,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
    position: 'relative', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
}));

const SimChip = styled(Chip)(({ selected }) => ({
    borderRadius: '12px', fontWeight: selected ? 700 : 500, fontSize: '0.8rem',
    transition: 'all 0.2s ease', cursor: 'pointer',
    ...(selected && { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }),
    ...(!selected && { '&:hover': { background: '#e2e8f0' } }),
}));

// All 50 M2M SIM MSISDN numbers
const ALL_M2M_SIMS = [
    '5754182932737','5754182932738','5754182932739','5754182932740','5754182932741',
    '5754182932742','5754182932743','5754182932744','5754182932745','5754182932746',
    '5754182932747','5754182932748','5754182932749','5754182932750','5754182932751',
    '5754182932752','5754182932753','5754182932754','5754182932755','5754182932756',
    '5754182932757','5754182932758','5754182932759','5754182932760','5754182932761',
    '5754182932762','5754182932763','5754182932764','5754182932765','5754182932766',
    '5754182932767','5754182932768','5754182932769','5754182932770','5754182932771',
    '5754182932772','5754182932773','5754182932774','5754182932775','5754182932776',
    '5754182932777','5754182932778','5754182932779','5754182932780','5754182932781',
    '5754182932782','5754182932783','5754182932784','5754182932785','5754182932786',
];

const QUICK_COMMANDS = ['SHOW CONFIG', 'RESET', 'STATUS', 'GET LOCATION', 'FIRMWARE VERSION', 'REBOOT'];

const SmsPage = () => {
    const [sims, setSims] = useState([]);
    const [selectedSim, setSelectedSim] = useState('');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [polling, setPolling] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [newSimDialog, setNewSimDialog] = useState(false);
    const [newSimNumber, setNewSimNumber] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [searchFilter, setSearchFilter] = useState('');
    const [directory, setDirectory] = useState([]);          // SIM → machine/site map
    const [siteFilter, setSiteFilter] = useState('ALL');     // site-location filter
    const [selectMode, setSelectMode] = useState(false);     // group-message selection
    const [selectedSims, setSelectedSims] = useState(new Set());
    const [groupDialog, setGroupDialog] = useState(false);
    const [groupInput, setGroupInput] = useState('');
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

    // Build combined list. Priority: SIM directory (devices table — carries the
    // machine + site terminology) → message-thread stats → legacy pre-loaded SIMs.
    const buildSimList = useCallback((dbSims, dir) => {
        const simMap = new Map();
        (dir || []).forEach(d => {
            const key = String(d.sim_number).trim();
            simMap.set(key, {
                sim_number: key, total_messages: 0, sent_count: 0, received_count: 0, last_message_at: null,
                equipment_id: d.equipment_id, device_id: d.device_id,
                model: d.model_number, brand: d.brand,
                site: d.site_location || 'Warehouse', fleet_status: d.fleet_status,
            });
        });
        (dbSims || []).forEach(s => {
            const key = String(s.sim_number).trim();
            const existing = simMap.get(key);
            if (existing) {
                Object.assign(existing, {
                    total_messages: s.total_messages, sent_count: s.sent_count,
                    received_count: s.received_count, last_message_at: s.last_message_at,
                });
            } else {
                simMap.set(key, { ...s, sim_number: key, site: 'Unassigned' });
            }
        });
        ALL_M2M_SIMS.forEach(num => {
            if (!simMap.has(num)) {
                simMap.set(num, { sim_number: num, total_messages: 0, sent_count: 0, received_count: 0, last_message_at: null, site: 'Unassigned' });
            }
        });
        return Array.from(simMap.values());
    }, []);

    const fetchSims = useCallback(async () => {
        try {
            const [simsRes, dirRes] = await Promise.allSettled([
                client.get('/sms/sims'),
                client.get('/sms/directory'),
            ]);
            const dbSims = simsRes.status === 'fulfilled' && simsRes.value.success ? simsRes.value.data : [];
            const dir = dirRes.status === 'fulfilled' && dirRes.value.success ? dirRes.value.data : [];
            setDirectory(dir || []);
            setSims(buildSimList(dbSims || [], dir || []));
        } catch (err) {
            console.error('Error fetching SIMs:', err);
            setSims(buildSimList([], []));
        }
    }, [buildSimList]);

    const fetchMessages = useCallback(async (sim) => {
        if (!sim) return;
        setLoading(true);
        try {
            const res = await client.get(`/sms/messages/${encodeURIComponent(sim)}`);
            if (res.success) setMessages(res.data || []);
        } catch (err) { console.error('Error fetching messages:', err); }
        finally { setLoading(false); }
    }, []);

    const pollForReplies = useCallback(async () => {
        if (!selectedSim) return;
        try {
            const lastMsg = messages.filter(m => m.direction === 'RECEIVED').pop();
            const since = lastMsg ? lastMsg.created_at : '';
            const res = await client.get(`/sms/messages/${encodeURIComponent(selectedSim)}/new`, { params: { since } });
            if (res.success && res.hasNew) fetchMessages(selectedSim);
        } catch (err) { /* silent */ }
    }, [selectedSim, messages, fetchMessages]);

    useEffect(() => { fetchSims(); }, [fetchSims]);

    // Deep link: /sms?sim=<number> (e.g. the SMS button on a machine's detail
    // page) opens that SIM's thread directly.
    const location = useLocation();
    useEffect(() => {
        const simParam = new URLSearchParams(location.search).get('sim');
        if (simParam && simParam.trim()) setSelectedSim(simParam.trim());
    }, [location.search]);
    useEffect(() => { if (selectedSim) fetchMessages(selectedSim); }, [selectedSim, fetchMessages]);
    useEffect(() => {
        if (selectedSim && polling) {
            pollRef.current = setInterval(pollForReplies, 30000);
            return () => clearInterval(pollRef.current);
        }
    }, [selectedSim, polling, pollForReplies]);

    const handleSend = async (msgOverride) => {
        const msg = msgOverride || input.trim();
        if (!msg || !selectedSim) return;
        setSending(true); setError(''); setSuccessMsg('');
        try {
            const res = await client.post('/sms/send', { simNumber: selectedSim, message: msg });
            if (res.success) {
                setInput('');
                setSuccessMsg(`Message sent (ID: ${res.messageId})`);
                fetchMessages(selectedSim);
                fetchSims();
                setPolling(true);
                setTimeout(() => setSuccessMsg(''), 4000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send SMS');
            setTimeout(() => setError(''), 5000);
        } finally { setSending(false); }
    };

    // Group send: one command to many SIMs (selected set, a site, or everything).
    // Every message goes out as a REAL SMS from the gateway phone — normal SIMs
    // are capped ~100 SMS/day, so the confirm shows the exact count.
    const handleGroupSend = async (command, targets, label) => {
        const msg = (command || '').trim();
        if (!msg || !targets.length) return;
        if (!window.confirm(`Send "${msg}" to ${targets.length} SIM${targets.length > 1 ? 's' : ''}${label ? ` — ${label}` : ''}?\n\nEach one is a real SMS from the gateway phone (daily quota ~100).`)) return;
        setBulkSending(true); setBulkProgress({ sent: 0, total: targets.length }); setError('');
        try {
            const res = await client.post('/sms/bulk-send', { simNumbers: targets, message: msg });
            if (res.success) {
                setBulkProgress({ sent: res.sent, total: res.total, failed: res.failed });
                setSuccessMsg(`✅ Group send complete: ${res.sent}/${res.total} sent`);
                setGroupDialog(false); setGroupInput('');
                setSelectedSims(new Set()); setSelectMode(false);
                fetchSims();
                setTimeout(() => { setSuccessMsg(''); setBulkProgress(null); }, 6000);
            }
        } catch (err) {
            setError('Group send failed: ' + (err.response?.data?.message || err.message));
        } finally { setBulkSending(false); }
    };

    const toggleSimSelected = (num) => {
        setSelectedSims(prev => {
            const next = new Set(prev);
            if (next.has(num)) next.delete(num); else next.add(num);
            return next;
        });
    };

    const handleAddSim = () => {
        if (newSimNumber.trim()) {
            setSelectedSim(newSimNumber.trim());
            setNewSimDialog(false); setNewSimNumber('');
            if (!sims.find(s => s.sim_number === newSimNumber.trim())) {
                setSims(prev => [{ sim_number: newSimNumber.trim(), total_messages: 0, sent_count: 0, received_count: 0 }, ...prev]);
            }
        }
    };

    const handleDeleteHistory = async () => {
        if (!selectedSim || !window.confirm(`Delete all messages for ${selectedSim}?`)) return;
        try {
            await client.delete(`/sms/messages/${encodeURIComponent(selectedSim)}`);
            setMessages([]); fetchSims();
        } catch (err) { setError('Failed to delete history'); }
    };

    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id); setTimeout(() => setCopiedId(null), 2000);
    };

    const formatTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
    const statusIcon = (s) => {
        if (s === 'SENT' || s === 'DELIVERED') return <CheckIcon sx={{ fontSize: 14, color: '#22c55e' }} />;
        if (s === 'FAILED') return <ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} />;
        return <PendingIcon sx={{ fontSize: 14, color: '#f59e0b' }} />;
    };

    // Distinct site locations for the filter (sites with devices first).
    const sites = React.useMemo(() => {
        const set = new Set();
        sims.forEach(s => set.add(s.site || 'Unassigned'));
        return Array.from(set).sort((a, b) => {
            if (a === 'Unassigned') return 1;
            if (b === 'Unassigned') return -1;
            return a.localeCompare(b);
        });
    }, [sims]);

    // Search matches SIM number OR machine terminology (equipment/model/site).
    const filteredSims = sims.filter(s => {
        const q = searchFilter.toLowerCase();
        const matchesSearch = !q ||
            s.sim_number.toLowerCase().includes(q) ||
            (s.equipment_id || '').toLowerCase().includes(q) ||
            (s.model || '').toLowerCase().includes(q) ||
            (s.site || '').toLowerCase().includes(q);
        const matchesSite = siteFilter === 'ALL' || (s.site || 'Unassigned') === siteFilter;
        return matchesSearch && matchesSite;
    });

    const selectedSimMeta = sims.find(s => s.sim_number === selectedSim);
    const bulkTargets = filteredSims.map(s => s.sim_number);
    const bulkLabel = siteFilter === 'ALL' ? `all ${bulkTargets.length} SIMs` : `${siteFilter} (${bulkTargets.length})`;

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 80px)', fontFamily: '"Inter","Segoe UI",sans-serif' }}>

            {/* ─── Left Panel ─── */}
            <Paper elevation={0} sx={{ width: 350, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SimCardIcon sx={{ color: '#2563eb' }} />
                            <Typography variant="h6" fontWeight={800} sx={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                M2M SMS
                            </Typography>
                        </Box>
                        <Chip label={`${sims.length} SIMs`} size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: '0.7rem' }} />
                    </Box>

                    {/* Bulk Send Button — scope follows the active site filter */}
                    <Button fullWidth variant="contained" startIcon={bulkSending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <BulkSendIcon />}
                        onClick={() => handleGroupSend('SHOW CONFIG', bulkTargets, bulkLabel)} disabled={bulkSending || bulkTargets.length === 0}
                        sx={{ mb: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                            background: 'linear-gradient(135deg,#059669,#047857)', '&:hover': { background: 'linear-gradient(135deg,#047857,#065f46)' } }}>
                        {bulkSending ? 'Sending...' : `Send SHOW CONFIG — ${bulkLabel}`}
                    </Button>

                    {bulkProgress && (
                        <Box sx={{ mb: 1 }}>
                            <LinearProgress variant="determinate" value={(bulkProgress.sent / bulkProgress.total) * 100}
                                sx={{ borderRadius: 4, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#059669' } }} />
                            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>{bulkProgress.sent}/{bulkProgress.total} sent{bulkProgress.failed ? ` · ${bulkProgress.failed} failed` : ''}</Typography>
                        </Box>
                    )}

                    {/* Site-location filter */}
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <Select value={siteFilter} onChange={e => setSiteFilter(e.target.value)} displayEmpty
                            renderValue={(v) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SiteIcon sx={{ fontSize: 15, color: '#2563eb' }} />
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                                        {v === 'ALL' ? 'All Sites' : v}
                                    </Typography>
                                </Box>
                            )}
                            sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                            <MenuItem value="ALL" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>All Sites ({sims.length})</MenuItem>
                            {sites.map(site => (
                                <MenuItem key={site} value={site} sx={{ fontSize: '0.8rem' }}>
                                    {site} ({sims.filter(s => (s.site || 'Unassigned') === site).length})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Search + group-select toggle */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <TextField fullWidth size="small" placeholder="SIM / machine / site..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SimCardIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                        <Tooltip title={selectMode ? 'Exit group select' : 'Group message (select SIMs)'}>
                            <IconButton size="small" onClick={() => { setSelectMode(!selectMode); if (selectMode) setSelectedSims(new Set()); }}
                                sx={{ bgcolor: selectMode ? '#2563eb' : '#f1f5f9', color: selectMode ? '#fff' : '#64748b', borderRadius: 2, width: 38,
                                    '&:hover': { bgcolor: selectMode ? '#1d4ed8' : '#e2e8f0' } }}>
                                {selectMode ? <CloseIcon sx={{ fontSize: 18 }} /> : <SelectModeIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Group-selection action bar */}
                    {selectMode && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#eff6ff', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox size="small" sx={{ p: 0.25 }}
                                checked={filteredSims.length > 0 && filteredSims.every(s => selectedSims.has(s.sim_number))}
                                indeterminate={selectedSims.size > 0 && !filteredSims.every(s => selectedSims.has(s.sim_number))}
                                onChange={(e) => setSelectedSims(e.target.checked ? new Set(filteredSims.map(s => s.sim_number)) : new Set())} />
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e40af', flex: 1 }}>
                                {selectedSims.size} selected
                            </Typography>
                            <Button size="small" variant="contained" startIcon={<GroupIcon sx={{ fontSize: 14 }} />}
                                disabled={selectedSims.size === 0 || bulkSending}
                                onClick={() => setGroupDialog(true)}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.7rem', borderRadius: 2, py: 0.3,
                                    background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                                Group Send
                            </Button>
                        </Box>
                    )}
                </Box>

                <List sx={{ flex: 1, overflowY: 'auto', p: 0.5 }}>
                    {filteredSims.map((sim) => (
                        <ListItemButton key={sim.sim_number} selected={!selectMode && selectedSim === sim.sim_number}
                            onClick={() => selectMode ? toggleSimSelected(sim.sim_number) : setSelectedSim(sim.sim_number)}
                            sx={{ borderRadius: 1.5, mb: 0.25, py: 0.75,
                                '&.Mui-selected': { bgcolor: '#eff6ff', borderLeft: '3px solid #2563eb', '&:hover': { bgcolor: '#dbeafe' } },
                                '&:hover': { bgcolor: '#f8fafc' } }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {selectMode ? (
                                    <Checkbox size="small" checked={selectedSims.has(sim.sim_number)} sx={{ p: 0 }} tabIndex={-1} disableRipple />
                                ) : (
                                    <SimCardIcon sx={{ fontSize: 18, color: selectedSim === sim.sim_number ? '#2563eb' : '#94a3b8' }} />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem' }} noWrap>
                                            {sim.equipment_id || sim.sim_number}
                                        </Typography>
                                        {sim.model && <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }} noWrap>· {sim.model}</Typography>}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        {sim.equipment_id && (
                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#94a3b8', display: 'block' }} noWrap>
                                                SIM {sim.sim_number}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" sx={{ fontSize: '0.63rem', color: sim.site === 'Unassigned' ? '#cbd5e1' : '#2563eb', display: 'flex', alignItems: 'center', gap: 0.25 }} noWrap>
                                            <SiteIcon sx={{ fontSize: 10 }} /> {sim.site || 'Unassigned'}
                                        </Typography>
                                    </Box>
                                }
                            />
                            {sim.total_messages > 0 && <Chip label={sim.total_messages} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#dbeafe', color: '#2563eb' }} />}
                        </ListItemButton>
                    ))}
                </List>
            </Paper>

            {/* ─── Right Panel ─── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
                {selectedSim ? (
                    <>
                        <Paper elevation={0} sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', borderRadius: 0 }}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {selectedSimMeta?.equipment_id || 'M2M SIM'}
                                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#64748b', ml: 1 }}>SIM {selectedSim}</Typography>
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip label="M2M" size="small" sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                                    {selectedSimMeta?.model && (
                                        <Chip icon={<MachineIcon sx={{ fontSize: '12px !important' }} />} label={`${selectedSimMeta.brand ? selectedSimMeta.brand + ' ' : ''}${selectedSimMeta.model}`}
                                            size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                    )}
                                    {selectedSimMeta?.site && selectedSimMeta.site !== 'Unassigned' && (
                                        <Chip icon={<SiteIcon sx={{ fontSize: '12px !important' }} />} label={selectedSimMeta.site}
                                            size="small" sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 600, fontSize: '0.65rem', height: 20, maxWidth: 260 }} />
                                    )}
                                    {selectedSimMeta?.device_id && (
                                        <Chip label={`TCU ${selectedSimMeta.device_id}`} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontFamily: 'monospace', fontSize: '0.62rem', height: 20 }} />
                                    )}
                                    {polling && <Chip label="● Polling" size="small" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: '0.7rem', height: 20, animation: `${pulse} 2s infinite` }} />}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={polling ? "Stop Polling" : "Start Polling"}>
                                    <IconButton size="small" onClick={() => setPolling(!polling)} sx={{ bgcolor: polling ? '#dcfce7' : '#f1f5f9' }}>
                                        <RefreshIcon fontSize="small" sx={{ color: polling ? '#16a34a' : '#64748b' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Refresh"><IconButton size="small" onClick={() => fetchMessages(selectedSim)} sx={{ bgcolor: '#f1f5f9' }}><HistoryIcon fontSize="small" sx={{ color: '#64748b' }} /></IconButton></Tooltip>
                                <Tooltip title="Delete History"><IconButton size="small" onClick={handleDeleteHistory} sx={{ bgcolor: '#fef2f2' }}><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></IconButton></Tooltip>
                            </Box>
                        </Paper>

                        <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9', bgcolor: '#fff' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1, fontWeight: 600 }}>Quick:</Typography>
                            {QUICK_COMMANDS.map(cmd => (<SimChip key={cmd} label={cmd} size="small" variant="outlined" onClick={() => handleSend(cmd)} />))}
                        </Box>

                        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mx: 3, mt: 1, borderRadius: 2 }}>{error}</Alert>}
                        {successMsg && <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ mx: 3, mt: 1, borderRadius: 2 }}>{successMsg}</Alert>}

                        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>
                            ) : messages.length === 0 ? (
                                <Box sx={{ textAlign: 'center', pt: 8 }}>
                                    <SmsIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" fontWeight={600}>No messages yet</Typography>
                                    <Typography variant="body2" color="text.secondary">Send a command to this M2M SIM to get started</Typography>
                                </Box>
                            ) : (
                                messages.map((msg) => (
                                    <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.direction === 'SENT' ? 'flex-end' : 'flex-start', mb: 2 }}>
                                        <Box sx={{ maxWidth: '75%' }}>
                                            <MessageBubble direction={msg.direction}>
                                                <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.6, fontFamily: 'monospace' }}>{msg.message}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.7 }}>{formatTime(msg.created_at)}</Typography>
                                                    {msg.direction === 'SENT' && statusIcon(msg.status)}
                                                </Box>
                                            </MessageBubble>
                                            <Tooltip title={copiedId === msg.id ? "Copied!" : "Copy"}>
                                                <IconButton size="small" onClick={() => handleCopy(msg.message, msg.id)} sx={{ mt: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                                    {copiedId === msg.id ? <CheckIcon sx={{ fontSize: 14 }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </Box>

                        <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', border: '2px solid #e2e8f0', borderRadius: 3, px: 2, transition: 'border-color 0.2s', '&:focus-within': { borderColor: '#2563eb' } }}>
                                <TextField fullWidth variant="standard" placeholder="Type SMS command..." value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                    InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem', py: 1.5 } }} />
                                <IconButton onClick={() => handleSend()} disabled={!input.trim() || sending}
                                    sx={{ bgcolor: input.trim() ? '#2563eb' : '#e2e8f0', color: input.trim() ? '#fff' : '#94a3b8', borderRadius: 2, ml: 1, width: 40, height: 40,
                                        '&:hover': { bgcolor: '#1d4ed8' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}>
                                    {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Paper>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                        <Box sx={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SimCardIcon sx={{ fontSize: 48, color: '#2563eb' }} />
                        </Box>
                        <Typography variant="h5" fontWeight={800}>M2M SIM Messaging</Typography>
                        <Typography color="text.secondary" sx={{ maxWidth: 440, textAlign: 'center' }}>
                            {sims.length} SIMs — each mapped to its machine and site. Pick a SIM to chat,
                            filter by site location, or use the ✓ group-select to message several machines at once.
                        </Typography>
                        <Button variant="contained" startIcon={<BulkSendIcon />} onClick={() => handleGroupSend('SHOW CONFIG', bulkTargets, bulkLabel)}
                            disabled={bulkTargets.length === 0}
                            sx={{ mt: 2, borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 4, py: 1.2,
                                background: 'linear-gradient(135deg,#059669,#047857)', boxShadow: '0 4px 14px rgba(5,150,105,0.3)' }}>
                            Send SHOW CONFIG — {bulkLabel}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Group Message Dialog */}
            <Dialog open={groupDialog} onClose={() => setGroupDialog(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 460 } }}>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ color: '#2563eb' }} /> Group Message — {selectedSims.size} SIM{selectedSims.size > 1 ? 's' : ''}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5, maxHeight: 90, overflowY: 'auto' }}>
                        {Array.from(selectedSims).map(num => {
                            const meta = sims.find(s => s.sim_number === num);
                            return <Chip key={num} label={meta?.equipment_id || num} size="small" onDelete={() => toggleSimSelected(num)}
                                sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#eff6ff', color: '#1e40af' }} />;
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                        {QUICK_COMMANDS.map(cmd => (
                            <SimChip key={cmd} label={cmd} size="small" variant="outlined" selected={groupInput === cmd}
                                onClick={() => setGroupInput(cmd)} />
                        ))}
                    </Box>
                    <TextField fullWidth multiline minRows={2} label="SMS command" value={groupInput}
                        onChange={(e) => setGroupInput(e.target.value)} placeholder="Type the command to send to every selected SIM..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Each SIM gets a separate real SMS from the gateway phone (daily quota ~100).
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGroupDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" disabled={!groupInput.trim() || bulkSending}
                        startIcon={bulkSending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleGroupSend(groupInput, Array.from(selectedSims), 'selected group')}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                        Send to {selectedSims.size}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* New SIM Dialog */}
            <Dialog open={newSimDialog} onClose={() => setNewSimDialog(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Add M2M SIM Number</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Enter the 13-digit MSISDN of the M2M SIM.</Typography>
                    <TextField fullWidth label="MSISDN Number" value={newSimNumber} onChange={(e) => setNewSimNumber(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddSim(); }} placeholder="e.g. 5754182932737" variant="outlined"
                        InputProps={{ startAdornment: <InputAdornment position="start"><SimCardIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNewSimDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddSim} disabled={!newSimNumber.trim()}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>Start Messaging</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SmsPage;
